import { MutationCache, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query'
import { reportFailure } from '../runtime/report'

/** A key's first segment names the resource (`subscriptions`); the rest are ids. */
function resourceOf(key: QueryKey | undefined): string | undefined {
  const first = key?.[0]
  return typeof first === 'string' ? first : undefined
}

/**
 * ONE QueryClient for the whole page.
 *
 * Each component mounts into its own React root, so they cannot share context
 * the usual way. Holding the client at module scope and handing the same
 * instance to every root's provider makes the server-state cache page-wide:
 * two widgets asking for the same key issue one request, and an invalidation
 * from any widget refreshes all of them.
 *
 * Every request the widgets make goes through it, which also makes it the one
 * place to report failures: each component's own onError only decides what
 * the visitor reads.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => reportFailure(error, { query: resourceOf(query.queryKey) }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      reportFailure(error, { query: resourceOf(mutation.options.mutationKey) }),
  }),
  defaultOptions: {
    queries: {
      // Marketing pages get opened and left in background tabs for a long
      // time. Refetching on every focus would hammer the API for no benefit.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
})
